import { Iterable } from 'immutable';
import { find, some } from 'lodash';
import enums from '../enum';
import componentServices from "Scripts/componentServices";
import { IChangeOrder } from "Scripts/models/changeOrderModel";
import { IContractor } from "Scripts/models/contractorModel";
import { IEmployee } from "Scripts/models/employeeModel";
import { IJobCost } from "Scripts/models/jobCostModel";
import { IJob } from 'Scripts/models/jobModel';
import { SiteVisitEmployeeRole } from 'Scripts/models/siteVisitEmployeeModel';
import { ISiteVisit, SiteVisitStatus, SiteVisitType } from "Scripts/models/siteVisitModel";
import { getCollection, ModelCollection } from 'Scripts/store';
import { IVisitable } from 'Scripts/models/visitable';
import { IContractSelection } from 'Scripts/models/operationsModelFacade';
import { ISiteVisitJobCost } from 'Scripts/models/siteVisitJobCostModel';
import { IInstallSheet } from 'Scripts/models/installSheetModel';
import { getInstallSheets } from 'Scripts/schemaHelper';

export interface IOperationsItem extends IVisitable {
		key: string;
		jobKey: string;
        jobCostKey: string;
        changeOrderKey: string;
		name: string;
		relayNumber: string;
		accountingId: string;
		clientContactName: string;
		address: string;
		deliveryVisitKey?: string;
		deliveryCompanyKey?: string;
		deliveryCompanyName: string;
		deliveryDate?: Date;
		deliveryStatus?: SiteVisitStatus;
		fieldSupervisorName: string;
		fieldSupervisorKey: string;
		installerName: string;
		installerKey: string;
		installVisitKey?: string;
		installStartDate?: Date;
		installStatus?: SiteVisitStatus;
		installSheets: IInstallSheet[];
		orderDate?: Date;
		builderJobNumber: string;
        currentStatus: string;
		companyVariant: string;
	}

export interface IOperationsHelper {
	getOperationsItems(): IOperationsItem[];
	getVisitables(jobKey: string): IOperationsItem[];
	getContractSelectionsForVisit(contracts: IJobCost[], visits: ISiteVisit[], siteVisitType: (SiteVisitType.delivery | SiteVisitType.install), currentVisit?: ISiteVisit) : IContractSelection[];
}

// Note: factory parameters are accessor functions
const operationsHelper = function (store) {
	const instance: IOperationsHelper = {
		getOperationsItems () {
			const statuses = ['order-processed', 'order-delivered', 'installed', 'inspected'];
			const employees = getCollection({ store: store(), collection: ModelCollection.employees });
			const jobs = getCollection({ store: store(), collection: ModelCollection.jobs });
			const jobCosts = getCollection({ store: store(), collection: ModelCollection.jobCosts });
			const siteVisits = getCollection({ store: store(), collection: ModelCollection.siteVisits });
			const deliveryCompanies = getCollection({  store: store(), collection: ModelCollection.contractors}).filter(c => (c.type & enums.contractorTypes.Delivery_Company) === enums.contractorTypes.Delivery_Company);
			const contractItems:IOperationsItem[] = [];

			jobCosts.forEach(jc => {
				let currentStatus = jc.currentStatusItem?.status;

				if(statuses.indexOf(currentStatus) !== -1) {
					contractItems.push(mapOperationsItem({ employees, jobs, siteVisits, deliveryCompanies, contract: jc, currentStatus }));

					for(const co of jc.changeOrders.toArray()) {
						currentStatus = co.currentStatusItem?.status;
                        
						if(statuses.indexOf(currentStatus) !== -1) {
							contractItems.push(mapOperationsItem({ employees, jobs, siteVisits, deliveryCompanies, contract: jc, changeOrder: co, currentStatus }));
						}
					}
				}

			});

			return contractItems;
		},
		getVisitables(jobKey: string): IOperationsItem[] {
			const statuses = ['order-processed'];
			const employees = getCollection({ store: store(), collection: ModelCollection.employees });
			const jobs = getCollection({ store: store(), collection: ModelCollection.jobs });
			const jobCosts = getCollection({ store: store(), collection: ModelCollection.jobCosts }).filter(jc => jc.jobKey === jobKey);
			const siteVisits = getCollection({ store: store(), collection: ModelCollection.siteVisits });
			const deliveryCompanies = getCollection({  store: store(), collection: ModelCollection.contractors}).filter(c => (c.type & enums.contractorTypes.Delivery_Company) === enums.contractorTypes.Delivery_Company);
			const visitables = [];

			jobCosts.forEach(jc => {
				let isVisitable = !!jc.getMostRecentStatusByStatus(statuses);

				if(isVisitable) {
					let currentStatus = jc.currentStatusItem?.status;
					visitables.push(mapOperationsItem({ employees, jobs, siteVisits, deliveryCompanies, contract: jc, currentStatus }));

					for(const co of jc.changeOrders.toArray()) {
						isVisitable = !!co.getMostRecentStatusByStatus(statuses);
						
						if(statuses.indexOf(currentStatus) !== -1) {
							currentStatus = co.currentStatusItem?.status;
							visitables.push(mapOperationsItem({ employees, jobs, siteVisits, deliveryCompanies, contract: jc, changeOrder: co, currentStatus }));
						}
					}
				}

			});

			return visitables;
		},
		// (DR): this expects that the list of visits will be filtered by visit type
		getContractSelectionsForVisit(contracts: IJobCost[], visits: ISiteVisit[], siteVisitType: (SiteVisitType.delivery | SiteVisitType.install), currentVisit?: ISiteVisit) {
			const selections:IContractSelection[] = [];

			contracts.forEach(c => {
				let selected = false;
			
				if(siteVisitType === SiteVisitType.delivery && c.requiresDelivery) {
					selected = currentVisit && some(currentVisit.jobCosts, jc => jc.jobCostKey === c.key && !jc.changeOrder);

					selections.push({
						contract: c,
						selected,
						disabled: !selected && visits.some(v => some(v.jobCosts, (jc:ISiteVisitJobCost) => jc.jobCostKey === c.key && !jc.changeOrderKey))
					});
				}

				if(siteVisitType === SiteVisitType.install && c.requiresInstall) {
					// contract installations are per install sheet
					const sheets = getInstallSheets(c).toArray();
					sheets.forEach(sheet => {
						selected = currentVisit && some(currentVisit.jobCosts, jc => jc.jobCostKey === c.key && jc.changeOrder?.key === jc.changeOrderKey && jc.installSheetKey === sheet.key);
						let disabled = currentVisit && !selected; // if it is selected it should not be disabled

						if(!currentVisit || disabled) { // if it is disabled because it is not selected, check to see if it can be enabled because it is not selected in any other visit
							disabled = visits.filter(v => !currentVisit || v.key !== currentVisit.key).some(v => some(v.jobCosts, (jc:ISiteVisitJobCost) => jc.installSheetKey === sheet.key));
						}

						selections.push({
							contract: c,
							selected,
							disabled,
							installSheet: sheet
						});
					});
				}

				c.changeOrders.toArray().forEach(co => {
					if(co.getMostRecentStatusByStatus('order-processed') && ((siteVisitType === SiteVisitType.delivery && co.requiresDelivery) || (siteVisitType === SiteVisitType.install && co.requiresInstall))) {
						selected = currentVisit && some(currentVisit.jobCosts, jc => jc.changeOrderKey === co.key);

						selections.push({
							contract: c,
							changeOrder: co,
							selected,
							disabled: visits.some(v => some(v.jobCosts, (jc:ISiteVisitJobCost) => jc.changeOrderKey === co.key))
						});
					}
				});
			});

			return selections;
		}
	};

	return instance;
};

componentServices.set('operationsHelper', operationsHelper, ['store']);

function mapOperationsItem({ employees, jobs, contract, changeOrder, siteVisits, deliveryCompanies, currentStatus }: 
    { employees: Iterable<string, IEmployee>, jobs: Iterable<string, IJob>, siteVisits: Iterable<string, ISiteVisit>, deliveryCompanies: Iterable<string, IContractor>, contract: IJobCost, changeOrder?: IChangeOrder, currentStatus: string }): IOperationsItem {
	const job = jobs.get(contract.jobKey);
	const installVisit = changeOrder? 
		siteVisits.filter(v => v.jobKey === job.key && v.type === SiteVisitType.install && some(v.jobCosts, jc => jc.changeOrderKey === changeOrder.key)).first(): 
		siteVisits.filter(v => v.jobKey === job.key && v.type === SiteVisitType.install && some(v.jobCosts, jc => jc.jobCostKey === contract.key && !jc.changeOrderKey)).first();
	const installerKey = find(installVisit?.employees, e => e.role === SiteVisitEmployeeRole.primary)?.employeeKey;
	const installer: IEmployee = employees?.get(installerKey);
	const installSheets: IInstallSheet[] = getInstallSheets(contract)?.toArray() || []; 
	const deliveryVisit = changeOrder? 
		siteVisits.filter(v => v.jobKey === job.key && v.type === SiteVisitType.delivery && some(v.jobCosts, jc => jc.changeOrderKey === changeOrder.key)).first(): 
		siteVisits.filter(v => v.jobKey === job.key && v.type === SiteVisitType.delivery && some(v.jobCosts, jc => jc.jobCostKey === contract.key && !jc.changeOrderKey)).first();
	const deliveryCompany = deliveryCompanies.find(c => c.key === deliveryVisit?.deliverer);
	const fieldSupervisorKey = job.fieldSupervisorKey;
	const fieldSupervisor: IEmployee = employees?.get(fieldSupervisorKey);
	const orderDate: Date = contract.getMostRecentStatusByStatus('order-processed')?.dateCreated;

	return {
		key: changeOrder?.key??contract.key,
		jobKey: contract.jobKey,
		jobCostKey: contract.key,
		changeOrderKey: changeOrder?.key,
		relayNumber: (changeOrder??contract).relayNumber,
		accountingId: changeOrder?.accountingId??contract.accountingId??'-',
		name: (changeOrder??contract).name,
		clientContactName: job.primaryClientContactName?? '-',
		address: job.workSiteAddress?.street??'-',
		deliveryVisitKey: deliveryVisit?.key,
		deliveryCompanyKey: deliveryVisit?.deliverer,
		deliveryCompanyName: deliveryCompany?.name??'-',
		deliveryDate: deliveryVisit?.scheduledDate,
		deliveryStatus: deliveryVisit?.currentStatus,
		fieldSupervisorName: fieldSupervisor?.fullName??'-',
		fieldSupervisorKey,
		installerName: installer?.fullName??'-',
		installerKey: installerKey,
		installVisitKey: installVisit?.key,
		installStartDate: installVisit?.scheduledDate,
		installStatus: installVisit?.currentStatus,
		orderDate,
		builderJobNumber: job.builderJobNumber??'-',
		currentStatus,
		requiresDelivery: (changeOrder??contract).requiresDelivery,
		requiresInstall: (changeOrder??contract).requiresInstall,
		companyVariant: contract.companyVariant,
		installSheets
	};
}